import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod/v4'
import { startAuthentication } from '@simplewebauthn/browser'
import { useOptimistic, useState, useTransition } from 'react'
import { data, Form, redirect, Link, useNavigate, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
// No external providers configured
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import {
  findUserByIdentifier,
  loginCodeVerificationType,
} from '#app/utils/login-code.server.ts'
import { getErrorMessage, useIsPending } from '#app/utils/misc.tsx'
import { type SEOHandle } from '#app/utils/seo.server.ts'
import { PasswordSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/login.ts'
import { sendLoginCode } from './login-code.server.tsx'
import { handleNewSession } from './login.server.ts'
import { getRedirectToUrl } from './verify.server.ts'

export const handle: SEOHandle = {
  getSitemapEntries: () => null,
}

const identifierField = z
  .string({ error: 'Enter your email or username' })
  .trim()
  .min(1, 'Enter your email or username')

const LoginFormSchema = z.object({
  identifier: identifierField,
  password: PasswordSchema,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
})

const SendCodeFormSchema = z.object({
  identifier: identifierField,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
})

const AuthenticationOptionsSchema = z.object({
  options: z.object({ challenge: z.string() }),
}) satisfies z.ZodType<{ options: PublicKeyCredentialRequestOptionsJSON }>

type LoginMethod = 'password' | 'send-code' | 'have-code'

/**
 * Which button was used. Pressing Enter uses the hidden default button
 * ('auto'), which logs in with a password if one was typed and otherwise
 * emails a code. 'have-code' skips sending: the member already holds a code
 * (for example one a program lead gave them), and requesting a new one would
 * replace it.
 */
function getLoginMethod(formData: FormData): LoginMethod {
  const method = formData.get('method')
  if (method === 'send-code' || method === 'password' || method === 'have-code')
    return method
  return formData.get('password') ? 'password' : 'send-code'
}

export async function loader({ request }: Route.LoaderArgs) {
  await requireAnonymous(request)
  return {}
}

export async function action({ request }: Route.ActionArgs) {
  await requireAnonymous(request)
  const formData = await request.formData()
  await checkHoneypot(formData)

  if (getLoginMethod(formData) === 'have-code') {
    const submission = parseWithZod(formData, { schema: SendCodeFormSchema })
    if (submission.status !== 'success') {
      return data(
        { result: submission.reply() },
        { status: submission.status === 'error' ? 400 : 200 },
      )
    }
    const { identifier, redirectTo, remember } = submission.value
    // No code is created or emailed, so nothing an earlier code depends on changes
    const verifyUrl = getRedirectToUrl({
      request,
      type: loginCodeVerificationType,
      target: identifier.toLowerCase(),
      redirectTo,
      remember: remember ?? false,
    })
    verifyUrl.searchParams.set('sent', '0')
    return redirect(`${verifyUrl.pathname}${verifyUrl.search}`)
  }

  if (getLoginMethod(formData) === 'send-code') {
    const submission = parseWithZod(formData, { schema: SendCodeFormSchema })
    if (submission.status !== 'success') {
      return data(
        { result: submission.reply() },
        { status: submission.status === 'error' ? 400 : 200 },
      )
    }
    const { identifier, redirectTo, remember } = submission.value
    // The code always goes to the email on file, and the response is the same
    // whether or not the account exists.
    const user = await findUserByIdentifier(identifier)
    await sendLoginCode({ request, user, redirectTo, remember: remember ?? false })
    const verifyUrl = getRedirectToUrl({
      request,
      type: loginCodeVerificationType,
      target: identifier.toLowerCase(),
      redirectTo,
      remember: remember ?? false,
    })
    return redirect(`${verifyUrl.pathname}${verifyUrl.search}`)
  }

  const submission = await parseWithZod(formData, {
    schema: (intent) =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null }

        const user = await findUserByIdentifier(data.identifier)
        const session = user
          ? await login({ username: user.username, password: data.password })
          : null
        if (!session) {
          ctx.addIssue({
            code: 'custom',
            message: 'Invalid username or password',
          })
          return z.NEVER
        }

        return { ...data, session }
      }),
    async: true,
  })

  if (submission.status !== 'success' || !submission.value.session) {
    return data(
      { result: submission.reply({ hideFields: ['password'] }) },
      { status: submission.status === 'error' ? 400 : 200 },
    )
  }

  const { session, remember, redirectTo } = submission.value

  return handleNewSession({
    request,
    session,
    remember: remember ?? false,
    redirectTo,
  })
}

export default function LoginPage({ actionData }: Route.ComponentProps) {
  const isPending = useIsPending()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')
  // Password entry is one step away, kept in the URL so it survives a refresh
  const passwordMode = searchParams.get('method') === 'password'
  const modeSearch = (method: 'password' | null) => {
    const next = new URLSearchParams(searchParams)
    if (method) next.set('method', method)
    else next.delete('method')
    const query = next.toString()
    return query ? `?${query}` : '?'
  }
  const [form, fields] = useForm<z.input<typeof LoginFormSchema>>({
    id: 'login-form',
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: { redirectTo, remember: 'on' },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      // a code request needs no password; the cast keeps the form's field types
      const schema =
        getLoginMethod(formData) === 'password'
          ? LoginFormSchema
          : (SendCodeFormSchema as unknown as typeof LoginFormSchema)
      return parseWithZod(formData, { schema })
    },
    shouldRevalidate: 'onBlur',
  })
  const status = isPending ? 'pending' : (form.status ?? 'idle')

  return (
    <div className="flex min-h-full flex-col justify-center px-6 pt-12 pb-32 md:pt-20">
      <div className="mx-auto w-full max-w-sm">
        <div className="space-y-2 text-center">
          <h1 className="text-h3">Welcome back</h1>
          <p className="text-body-sm text-muted-foreground">
            {passwordMode
              ? 'Log in with your password.'
              : "We'll email you a one-time code. No password needed."}
          </p>
        </div>

        <Form method="POST" className="mt-8" {...getFormProps(form)}>
          <HoneypotInputs />
          <Field
            labelProps={{ children: 'Username or email' }}
            inputProps={{
              ...getInputProps(fields.identifier, { type: 'text' }),
              autoFocus: true,
              className: 'lowercase',
              autoComplete: 'username',
              autoCapitalize: 'none',
            }}
            errors={fields.identifier.errors}
          />

          {passwordMode ? (
            <div>
              <Field
                labelProps={{ children: 'Password' }}
                inputProps={{
                  ...getInputProps(fields.password, { type: 'password' }),
                  autoComplete: 'current-password',
                }}
                errors={fields.password.errors}
              />
              <div className="-mt-3 mb-4 text-right">
                <Link
                  to="/forgot-password"
                  className="text-body-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
          ) : null}

          <CheckboxField
            labelProps={{
              htmlFor: fields.remember.id,
              children: 'Keep me signed in on this device',
            }}
            buttonProps={getInputProps(fields.remember, { type: 'checkbox' })}
            errors={fields.remember.errors}
          />

          <input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
          <ErrorList errors={form.errors} id={form.errorId} />

          {/* default button, so Enter picks the method (see getLoginMethod) */}
          <button
            type="submit"
            name="method"
            value={passwordMode ? 'password' : 'auto'}
            formNoValidate
            tabIndex={-1}
            aria-hidden
            className="sr-only"
          />
          {passwordMode ? (
            <StatusButton
              className="w-full"
              size="lg"
              status={status}
              type="submit"
              name="method"
              value="password"
              disabled={isPending}
            >
              Log in
            </StatusButton>
          ) : (
            <StatusButton
              className="w-full"
              size="lg"
              status={status}
              type="submit"
              name="method"
              value="send-code"
              formNoValidate
              disabled={isPending}
            >
              Email me a code
            </StatusButton>
          )}

          <div className="text-body-sm mt-4 flex flex-col items-center gap-2">
            <Link
              to={modeSearch(passwordMode ? null : 'password')}
              replace
              preventScrollReset
              className="text-muted-foreground hover:text-foreground underline underline-offset-4"
            >
              {passwordMode ? 'Email me a code instead' : 'Use a password instead'}
            </Link>
          </div>

          <div className="bg-muted/60 mt-6 rounded-lg p-4">
            <p className="text-body-sm font-medium">Already have a code?</p>
            <p className="text-body-xs text-muted-foreground mt-1">
              If your program lead gave you one, enter it here. Asking for a new
              code cancels any earlier one.
            </p>
            <button
              type="submit"
              name="method"
              value="have-code"
              formNoValidate
              disabled={isPending}
              className="text-body-sm text-brand mt-2 cursor-pointer font-medium underline underline-offset-4 disabled:opacity-50"
            >
              Enter my code
            </button>
          </div>
        </Form>

        <div className="text-body-xs text-muted-foreground my-6 flex items-center gap-3">
          <hr className="flex-1" />
          <span>or</span>
          <hr className="flex-1" />
        </div>
        <PasskeyLogin
          redirectTo={redirectTo}
          remember={fields.remember.value === 'on'}
        />
      </div>
    </div>
  )
}

const VerificationResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('success'),
    location: z.string(),
  }),
  z.object({
    status: z.literal('error'),
    error: z.string(),
  }),
])

function PasskeyLogin({
  redirectTo,
  remember,
}: {
  redirectTo: string | null
  remember: boolean
}) {
  const [isPending] = useTransition()
  const [error, setError] = useState<string | null>(null)
  const [passkeyMessage, setPasskeyMessage] = useOptimistic<string | null>(
    'Login with a passkey',
  )
  const navigate = useNavigate()

  async function handlePasskeyLogin() {
    try {
      setPasskeyMessage('Generating Authentication Options')
      // Get authentication options from the server
      const optionsResponse = await fetch('/webauthn/authentication')
      const json = await optionsResponse.json()
      const { options } = AuthenticationOptionsSchema.parse(json)

      setPasskeyMessage('Requesting your authorization')
      const authResponse = await startAuthentication({ optionsJSON: options })
      setPasskeyMessage('Verifying your passkey')

      // Verify the authentication with the server
      const verificationResponse = await fetch('/webauthn/authentication', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authResponse, remember, redirectTo }),
      })

      const verificationJson = await verificationResponse.json().catch(() => ({
        status: 'error',
        error: 'Unknown error',
      }))

      const parsedResult =
        VerificationResponseSchema.safeParse(verificationJson)
      if (!parsedResult.success) {
        throw new Error(parsedResult.error.message)
      } else if (parsedResult.data.status === 'error') {
        throw new Error(parsedResult.data.error)
      }
      const { location } = parsedResult.data

      setPasskeyMessage("You're logged in! Navigating...")
      await navigate(location ?? '/')
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      setError(`Failed to authenticate with passkey: ${errorMessage}`)
    }
  }

  return (
    <form action={handlePasskeyLogin}>
      <StatusButton
        id="passkey-login-button"
        aria-describedby="passkey-login-button-error"
        variant="outline"
        size="lg"
        className="w-full"
        status={isPending ? 'pending' : error ? 'error' : 'idle'}
        type="submit"
        disabled={isPending}
      >
        <span className="inline-flex items-center gap-1.5">
          <Icon name="passkey" />
          <span>{passkeyMessage}</span>
        </span>
      </StatusButton>
      <div className="mt-2">
        <ErrorList errors={[error]} id="passkey-login-button-error" />
      </div>
    </form>
  )
}

export const meta: Route.MetaFunction = () => {
  return [{ title: 'Login to Youth Rollcall' }]
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />
}
